// What Đề xuất lists for one period: one lesson when the period names it, never the whole tree.
// Only .ts-suffixed runtime imports, so node --test can load it.

import type { IztContentNode } from '../api/types';
import { contentKind, stripLeadingOrdinal } from '../config/content-types.ts';
import { i18n } from '../i18n/index.ts';
import type { RecentRow } from './recentRows.ts';

type ContentUnit = IztContentNode['sections'][number]['units'][number];

export interface PeriodSuggestion {
  unit: ContentUnit;
  courseId?: string;
}

export interface PeriodSignals {
  /** A planned IZ unit id; topic-lesson has no such field yet, so this is set only if one appears. */
  unitId?: string;
  name?: string;
  courseId?: string;
  /** The period's lesson name: its linked topic lesson, else the Sổ đầu bài note. */
  plannedName?: string;
  /** Units this class was taught before this period, newest first. */
  taughtUnitIds?: string[];
}

/** plan: the period's own lesson; next: after the last one taught; class: the home page pick. */
export type SuggestionSource = 'plan' | 'next' | 'class';

export interface PeriodSuggestions {
  source: SuggestionSource | null;
  rows: PeriodSuggestion[];
}

export const CLASS_SUGGESTION_LIMIT = 3;

function flatten(tree: IztContentNode[]): PeriodSuggestion[] {
  const rows: PeriodSuggestion[] = [];
  tree.forEach((course) => course.sections.forEach((section) => section.units.forEach((unit) => rows.push({ unit, courseId: course.id }))));
  return rows;
}

// unknown types are held back; a classified unit with no asset opens on an honest empty state
function suggestable(row: PeriodSuggestion): boolean {
  return contentKind(row.unit.type) !== 'other';
}

/** "Bài 3: Số hữu tỉ" and "3. Số hữu tỉ" compare equal; the plan and IZ only share titles. */
export function lessonKey(name: string | undefined): string {
  const text = stripLeadingOrdinal(String(name || '').normalize('NFC').toLowerCase());
  return text.replace(/^bài\s*\d{1,3}\s*[.:)-]?\s*/, '').replace(/\s+/g, ' ').trim();
}

/** The home page "Gợi ý bài giảng hôm nay" list for a class: the first classified units of its tree. */
export function classSuggestions(tree: IztContentNode[]): PeriodSuggestion[] {
  return flatten(tree).filter(suggestable).slice(0, CLASS_SUGGESTION_LIMIT);
}

/** The classified unit after the newest taught one the tree still holds, within that unit's course. */
function nextAfterTaught(all: PeriodSuggestion[], taught: string[]): PeriodSuggestion | null {
  for (let t = 0; t < taught.length; t += 1) {
    const at = all.map((row) => row.unit.id).indexOf(taught[t]);
    if (at < 0) continue;
    const course = all[at].courseId;
    const rest = all.slice(at + 1).filter((row) => row.courseId === course && suggestable(row));
    return rest[0] || null;
  }
  return null;
}

function byUnitId(all: PeriodSuggestion[], signals: PeriodSignals): PeriodSuggestion[] {
  const unitId = signals.unitId ? String(signals.unitId) : '';
  if (!unitId) return [];
  const hits = all.filter((row) => row.unit.id === unitId);
  // the plan's course wins when the same unit sits in two courses
  const preferred = hits.filter((hit) => hit.courseId === signals.courseId);
  if (hits.length > 0) return [preferred.length > 0 ? preferred[0] : hits[0]];
  return [{ unit: { id: unitId, name: signals.name || i18n.t('board:suggestions.periodLesson'), type: null, homeworkQuestion: null }, courseId: signals.courseId }];
}

/** Planned lesson, else the one after the last taught, else the home page list; empty only when the tree is. */
export function periodSuggestions(tree: IztContentNode[], signals: PeriodSignals): PeriodSuggestions {
  const all = flatten(tree);
  const planned = byUnitId(all, signals);
  if (planned.length > 0) return { source: 'plan', rows: planned };

  const next = nextAfterTaught(all, signals.taughtUnitIds || []);
  const key = lessonKey(signals.plannedName);
  if (key) {
    const named = all.filter((row) => suggestable(row) && lessonKey(row.unit.name) === key);
    // a title shared by several units ("Luyện tập") counts only when the class's next unit is one of them
    const pick = named.length === 1 ? named[0] : named.filter((row) => next !== null && row.unit.id === next.unit.id)[0];
    if (pick) return { source: 'plan', rows: [pick] };
  }
  if (next) return { source: 'next', rows: [next] };

  const rows = classSuggestions(tree);
  return { source: rows.length > 0 ? 'class' : null, rows };
}

/** Units the class was taught before this date, newest first; rows already filtered to this teacher. */
export function taughtUnitIds(rows: Array<Pick<RecentRow, 'unitId' | 'classId' | 'date'>>, classId: string, date: string): string[] {
  return rows.filter((row) => row.classId === classId && row.date < date).map((row) => row.unitId);
}

/** The name of a populated topic lesson, '' for a bare id. */
export function topicLessonName(value: unknown): string {
  if (!value || typeof value !== 'object') return '';
  return String((value as { name?: string }).name || '');
}
